<template>
  <pos-layout>
    <template #clock-right>
      <router-link
        :to="{ name: 'OrdersList' }"
        class="header-action-btn text-white font-weight-bold text-decoration-none"
        title="All Orders"
      >
        <svg class="icon icon-icon-order-invoice">
          <use xlink:href="#icon-icon-order-invoice" />
        </svg>
        <span class="header-action-btn__text">All Orders</span>
      </router-link>
      <router-link
        :to="{ name: 'Order', params: { tableID: null, id: 'new' } }"
        class="header-action-btn text-white font-weight-bold text-decoration-none"
        title="New Sale"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <line
            x1="12"
            y1="5"
            x2="12"
            y2="19"
          />
          <line
            x1="5"
            y1="12"
            x2="19"
            y2="12"
          />
        </svg>
        <span class="header-action-btn__text">New Sale</span>
      </router-link>
    </template>
    <div class="h-100 orders">
      <b-row class="h-100 no-gutters">
        <b-col
          :class="{hide: sidebarMob}"
          col
          cols="12"
          md="5"
          lg="4"
          class="h-100 orders-sidebar-mob"
        >
          <OrderDetailSidebar
            :order="currentOrder"
            :items="itemsAtCurrentOrder"
            :currency="currency"
            :order-no-text="displayOrderId"
            :show-refund-button="currentOrder.status === ORDER_STATUS.COMPLETED"
            :refund-disabled="currentOrder.isFullyRefund || !currentOrder.id || orderHasOnlyGiftCardProduct"
            :display-payment-details="displayPaymentDetailsList"
            surcharge-label="Non-Cash Fee"
            :item-key="(item, index) => 'item_' + index"
            :item-name="(item) => item.name"
            :item-price-text="(item) => getItemGrandTotal(item).toFixed(2)"
            :collapse-price-text="(item) => item.price ? item.price.toFixed(2) : '0.00'"
            :collapse-discount-text="(item) => item.discount"
            :total-text="(o) => o.grand_total ? orderFinalTotal(o).toFixed(2) : '0.00'"
            :discount-total-text="(o) => o.discount_total ? o.discount_total.toFixed(2) : '0'"
            :surcharge-value-text="(o) => parseFloat(o.surcharge_value).toFixed(2)"
            :balance-text="(o) => computedBalanceRemaining(o).toFixed(2)"
            :print-disabled="(o) => o.status === undefined || o.items.length === 0"
            @refund="showRefundModal"
            @cancel="cancelCurrentOrder"
            @toggle-details="isDisplayPaymentDetailsList"
            @print-check="printCheck()"
          >
            <template #item-modifiers="{ item }">
              <!-- Modifiers section -->
              <div
                v-if="item.modifiers && item.modifiers.length > 0"
                class="mod"
              >
                <div
                  v-for="modifier in item.modifiers"
                  :key="modifier.oryginal"
                  class="d-flex align-items-center justify-content-between sub text-nowrap"
                  v-show="currentOrder.status === ORDER_STATUS.CANCELLED || !modifier.void_status"
                >
                  <div class="text-left">
                    {{ modifier.name }}
                  </div>
                  <div class="text-right">
                    {{ currency }} {{ modifier.price.toFixed(2) }}
                  </div>
                </div>
              </div>
            </template>
            <template #item-discount="{ item }">
              <div
                class="d-flex align-items-center justify-content-between sub text-nowrap"
                v-if="item.discount"
              >
                <div class="text-left">
                  {{ calculateDiscount(item.price, item.discount) }} %
                </div>
                <div class="text-right">
                  {{ currency }} {{ item.discount ? (item.discount * item.quantity).toFixed(2) : "0.00" }}
                </div>
              </div>
            </template>
          </OrderDetailSidebar>
        </b-col>

        <b-col
          col
          cols="12"
          md="7"
          lg="8"
          class="h-100 orders-col"
        >
          <div
            :id="!isMobile && 'infinite-list'"
            class="products-wrap h-100 pt-0"
          >
            <div
              :id="isMobile && 'infinite-list'"
              class="section-body"
            >
              <div class="filter">
                <div class="filter-panel row filter-p">
                  <div class="col p-0 first-col position-relative">
                    <span
                      class="tab-count"
                      v-if="countAllPending>0"
                    >{{ countAllPending }}</span>
                    <b-button
                      class="w-100 first"
                      :class="{'active': statusFilter==ALL_PENDING || statusFilter==ORDER_STATUS.PENDING || statusFilter==ORDER_STATUS.OPEN}"
                      @click="statusFilter=ALL_PENDING"
                    >
                      <span>All Pending</span>
                    </b-button>
                  </div>
                  <div class="col p-0 middle-col position-relative">
                    <b-button
                      :class="{'active': statusFilter==ORDER_STATUS.COMPLETED}"
                      @click="statusFilter=ORDER_STATUS.COMPLETED"
                      class="w-100 middle"
                    >
                      <span>{{ $t("vieworderspopup.completed") }}</span>
                    </b-button>
                  </div>
                  <div class="col p-0 middle-col position-relative">
                    <b-button
                      :class="{'active': statusFilter==ORDER_STATUS.CANCELLED}"
                      @click="statusFilter=ORDER_STATUS.CANCELLED"
                      class="w-100 middle"
                    >
                      <span>{{ $t("vieworderspopup.cancelled") }}</span>
                    </b-button>
                  </div>
                  <div class="col p-0 middle-col position-relative">
                    <b-button
                      :class="{'active': statusFilter==ORDER_STATUS.REFUNDED}"
                      @click="statusFilter=ORDER_STATUS.REFUNDED"
                      class="w-100 last"
                    >
                      <span>{{ $t("vieworderspopup.refunded") }}</span>
                    </b-button>
                  </div>
                </div>

                <div
                  v-if="showDateTabs"
                  class="filter-panel filter-panel-sub justify-content-between row"
                >
                  <div
                    class="date-tab-group"
                    role="group"
                  >
                    <div class="date-tab">
                      <span
                        v-if="countAllPending>0"
                        class="tab-count"
                      >{{ countAllPending }}</span>
                      <button
                        type="button"
                        class="btn btn-outline-primary first"
                        :class="{'active': statusFilter===ALL_PENDING}"
                        @click="statusFilter=ALL_PENDING"
                      >
                        All
                      </button>
                    </div>
                    <div class="date-tab">
                      <span
                        v-if="countPending>0"
                        class="tab-count"
                      >{{ countPending }}</span>
                      <button
                        type="button"
                        class="btn btn-outline-primary middle"
                        :class="{'active': statusFilter===ORDER_STATUS.PENDING}"
                        @click="statusFilter=ORDER_STATUS.PENDING"
                      >
                        Pending
                      </button>
                    </div>
                    <div class="date-tab">
                      <span
                        v-if="statusOnlineCount(ORDER_STATUS.OPEN)>0"
                        class="tab-count"
                      >{{ statusOnlineCount(ORDER_STATUS.OPEN) }}</span>
                      <button
                        type="button"
                        class="btn btn-outline-primary last"
                        :class="{'active': statusFilter===ORDER_STATUS.OPEN}"
                        @click="statusFilter=ORDER_STATUS.OPEN"
                      >
                        Approved - Not Paid
                      </button>
                    </div>
                  </div>

                  <div
                    v-if="diningOptions.length > 1"
                    class="btn-group"
                    role="group"
                  >
                    <button
                      type="button"
                      :class="{'active': diningFilter===null}"
                      @click="diningFilter=null"
                      class="btn btn-outline-primary"
                    >
                      All
                    </button>
                    <button
                      v-for="(diningOption, dindex) in diningOptions"
                      :key="'dindex_'+dindex"
                      type="button"
                      :class="{'active': diningFilter===diningOption.name}"
                      @click="diningFilter=diningOption.name"
                      class="btn btn-outline-primary"
                    >
                      {{ diningOption.name }}
                    </button>
                  </div>

                  <div
                    v-if="showDateTabs"
                    class="date-tab-group ml-auto"
                    role="group"
                  >
                    <div class="date-tab">
                      <span
                        v-if="dateBucketCount('all')>0"
                        class="tab-count"
                      >{{ dateBucketCount('all') }}</span>
                      <button
                        type="button"
                        class="btn btn-outline-primary first"
                        :class="{'active': dateFilter==='all'}"
                        @click="dateFilter='all'"
                      >
                        All time
                      </button>
                    </div>
                    <div class="date-tab">
                      <span
                        v-if="dateBucketCount('today')>0"
                        class="tab-count"
                      >{{ dateBucketCount('today') }}</span>
                      <button
                        type="button"
                        class="btn btn-outline-primary middle"
                        :class="{'active': dateFilter==='today'}"
                        @click="dateFilter='today'"
                      >
                        Today
                      </button>
                    </div>
                    <div class="date-tab">
                      <span
                        v-if="dateBucketCount('future')>0"
                        class="tab-count"
                      >{{ dateBucketCount('future') }}</span>
                      <button
                        type="button"
                        class="btn btn-outline-primary last"
                        :class="{'active': dateFilter==='future'}"
                        @click="dateFilter='future';"
                      >
                        Future
                      </button>
                    </div>
                  </div>
                </div>

                <div class="filter-body">
                  <div class="oo-list">
                    <div
                      v-if="filteredOrders.length === 0 && !(isLoad || isProcessing)"
                      class="oo-empty text-center text-muted"
                    >
                      No orders available to display.
                    </div>
                    <OrderListCard
                      v-for="(order, index) in filteredOrders"
                      :key="'filteredOrders'+index"
                      :order="order"
                      :currency="currency"
                      :accent-class="accentClass(order)"
                      :selected="isSelected(order)"
                      :id-text="order.id"
                      amount-label="Amount"
                      :amount-text="order.grand_total ? orderFinalTotal(order).toFixed(2) : '0.00'"
                      :customer="order.customer || null"
                      :meta-lines="buildMetaLines(order)"
                      :info-cells="buildInfoCells(order)"
                      :show-actions="order.status === ORDER_STATUS.PENDING || order.status === ORDER_STATUS.OPEN"
                      :classes="cardClasses"
                      @card-click="currentOrder = order"
                    >
                      <template #pills>
                        <span
                          v-if="order.type === TYPE_KITCHENHUB_ORDER"
                          class="oti-source-pill oti-source-kitchenhub"
                        >{{ order.provider_name || "KitchenHub" }}</span>
                        <span
                          v-if="order.dining_option"
                          class="oti-dining"
                        >{{ order.dining_option }}</span>
                        <span
                          class="oti-status-pill"
                          :class="isOrderPaid(order) ? 'paid' : 'unpaid'"
                        >
                          {{ isOrderPaid(order) ? "Paid" : "Unpaid" }}
                        </span>
                      </template>
                      <template #actions>
                        <template v-if="order.status === ORDER_STATUS.PENDING">
                          <button
                            type="button"
                            class="btn btn-success"
                            @click.stop="approve(order)"
                          >
                            {{ order.type === TYPE_KITCHENHUB_ORDER ? "Accept" : "Approve" }}
                          </button>
                          <button
                            type="button"
                            class="btn btn-danger"
                            @click.stop="reject(order)"
                          >
                            Reject
                          </button>
                        </template>
                        <template v-if="order.status === ORDER_STATUS.OPEN">
                          <button
                            v-if="order.type === TYPE_KITCHENHUB_ORDER && order.is_kitchenhub_paid"
                            type="button"
                            class="btn btn-success"
                            @click.stop="completeKitchenHubOrder(order)"
                          >
                            Complete
                          </button>
                          <button
                            v-else
                            type="button"
                            class="btn btn-success"
                            @click.stop="pay(order)"
                          >
                            Pay
                          </button>
                        </template>
                      </template>
                    </OrderListCard>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <scale-loader
            :loading="isLoad || isProcessing"
            :color="'#007bff'"
          />
        </b-col>
      </b-row>
    </div>
    <SimpleInfoModal :id="'info-modal'">
      <template v-slot:title>
        {{ $t("order.not-found") }}
      </template>
      <template v-slot:info>
        {{ $t("order.not-found") }}. {{ $t("modal-info.try-again") }}
      </template>
    </SimpleInfoModal>
    <ConfirmActionModal
      :modal="confirmActionModal"
      @confirm-action="confirmAction"
    />
    <SuccessActionModal :modal="successActionModal" />
    <CustomerModal
      :view-customer="viewCustomer"
      :is-selected="true"
      @close-modal="clearViewCustomer"
    />
    <ProcessingModal />
    <ModalError
      :id="'modal-error-checkout'"
      :error="error"
    />

    <ConfirmModal
      :modal="confirmActionModal"
      @confirm-action="rejectConfirm"
    />
  </pos-layout>
</template>

<script>
import { ORDER_STATUS } from "@/helpers/order/status";
import {
	TYPE_KITCHENHUB_ORDER,
	resolveKitchenHubPaymentTypeName,
	resolveKitchenHubPaymentTypeId
} from "@/helpers/order/kitchenHubOrderMapper";

const TYPE_ONLINE_ORDER = 1;
const ONLINE_SOURCE_ORDER_TYPES = [TYPE_ONLINE_ORDER, TYPE_KITCHENHUB_ORDER];
import { getOrderFinalTotal } from "@/pricing";
import { getTimerValue, formatServeAt } from "@/helpers/order/listFormatting";
import { reconcilePartialRefunds } from "@/helpers/order/partialRefund";

// include the default style
import "vuejs-dialog/dist/vuejs-dialog.min.css";
import orderMixin from "../mixins/order/orderMixin";
import shiftMixin from "../mixins/shiftMixin";
import viewCustomerModalMixin from "../mixins/modals/viewCustomerModalMixin";
import CustomerModal from "@/modals/CustomerModal";
import SimpleInfoModal from "../components/SimpleInfoModal";
import OrderListCard from "../components/orders/OrderListCard";
import OrderDetailSidebar from "../components/orders/OrderDetailSidebar";
import PosLayout from "../layouts/PosLayout";
import ConfirmActionModal from "../modals/ConfirmActionModal";
import SuccessActionModal from "../modals/SuccessActionModal";
import { TRANSACTION_TYPE } from "@/helpers/order/transaction/types";
import syncMixin from "../mixins/syncMixin";
import printMixin from "../mixins/printMixin";
import ProcessingModal from "../modals/ProcessingModal";
import ModalError from "../modals/ModalError";
import { PAYMENT_BEHAVIOR } from "@/helpers/order/payment/behavior";
import { makeNARefund, makeNAVoid } from "@/helpers/payments/NABancard";
import ConfirmModal from "../modals/ConfirmModal";
import { mapGetters, mapActions} from 'vuex'
import { toFixedNoRounding } from "@/helpers/utils";
import ApiService from "@/core/services/api.service";

const sound = new Audio(require("@/assets/sounds/error-sound.mp3"));
// const soundNewOrder = new Audio(require("../assets/sounds/online_order_notificaiton.wav"));
export default {
	name: "OrderList",
	components: {
		PosLayout,
		SimpleInfoModal,
		OrderListCard,
		OrderDetailSidebar,
		ScaleLoader: () => import("vue-spinner/src/ScaleLoader.vue"),
		ConfirmActionModal,
		SuccessActionModal,
		CustomerModal,
		ProcessingModal,
		ModalError,
		ConfirmModal
	},
	mixins: [orderMixin, shiftMixin, viewCustomerModalMixin, syncMixin, printMixin],
	data () {
		return {
			ORDER_STATUS,
			TYPE_KITCHENHUB_ORDER,
			// Pseudo-status for the combined "All Pending" tab (Pending + Payment Pending)
			ALL_PENDING: "all_pending",
			sidebarMob: true,
			timer: 0,
			syncing: null,
			// Teardown handles for listeners/timers created in mounted()
			// (Phase 5 leak fixes).
			startupTimer: null,
			scrollContainer: null,
			onScrollHandler: null,
			// Reactive "current day" used by the Today/Future bucketing. A midnight
			// timer (and a visibility check) flips this so orders re-bucket when the
			// day changes, without relying on the order-refresh polling.
			currentDay: new Date().toDateString(),
			midnightTimer: null,
			baseURL: localStorage.baseURL,
			swiperOption: {
				slidesPerView: "auto",
				spaceBetween: 0,
				freeMode: true,
				slideToClickedSlide: false,
				pagination: {
					el: ".swiper-pagination",
					clickable: true
				}
			},
			storeInfo: null,
			itemIdCounter: 0,
			currentOrder: {
				id: 0,
				localID: 0,
				subtotal: 0,
				tax_total: 0,
				grand_total: 0,
				items: [],
				table: null,
				status: null,
				isFullyRefund: false,
				// order_gift_card_payment:[]
			},
			modalShow: false,
			rejectingOrder: null,
			bodyBgVariant: "light",
			popupPrice: ["popup-price"],
			popupPriceSidebar: ["popup-price-sidebar"],
			statusFilter: "all_pending",
			diningFilter: null,
			dateFilter: "all",
			displayPaymentDetailsList: false,
			searchValue: "",
			perPage: 20,
			countItems: 20,
			infoModal: {
				text: "Not Found"
			},
			confirmActionModal: {
				title: "",
				text: "",
				confirmActionText: "refund"
			},
			actionToConfirm: "",
			successActionModal: {
				title: "Success",
				text: "Order has been refunded"
			},
			isProcessing: false,
			error: {
				title: "Unknown error...",
				info: "Please, contact support"
			}
		};
	},
	computed: {
		...mapGetters({
			shouldDeductQuantity: 'orders/shouldDeductQuantity',
			refundedOrdersList: "orders/refundedOrdersList",
			products: "config/products",
		}),
		// True when the Ably realtime channel is connected (and not disabled).
		// When active, order updates arrive via realtime so the legacy 3-minute
		// polling and the Pusher-triggered refetches are redundant.
		isRealtimeActive () {
			const realtime = this.$store.state.realtime;
			return !!(realtime && realtime.isConnected && !realtime.isDisabled);
		},
		employee () {
			return this.$store.getters["employee"];
		},
		currency () {
			return this.$store.getters["config/currency"];
		},
		store () {
			return this.$store.getters["config/store"];
		},
		displayOrderId: function () {
			const orderId = this.currentOrder.id || this.currentOrder.local_id;

			// If order has a table, get the table name
			if (this.currentOrder.table) {
				const table = this.$store.getters["config/getTableById"](this.currentOrder.table);
				if (table && table.table_name) {
					return `${table.table_name} / ${orderId}`;
				}
			}

			return orderId;
		},
		diningOptions: function () {
			return this.terminal.dining_options.filter(o => o.online_ordering);
		},
		terminal () {
			return this.$store.getters["config/terminal"];
		},
		// Pure computed: derives the visible, filtered, sorted, paginated online
		// order list from the store. Phase 5 moved the partial-refund
		// reconciliation (which mutated store objects) out of here and into the
		// `filteredOrders` watcher (see helpers/order/partialRefund.js) so the
		// computed no longer produces side effects. Observable behavior preserved.
		filteredOrders: function () {
			return this.buildFilteredOrders().slice(0, this.countItems);
		},
		orders: function () {
			return this.$store.getters["orders/getAllOrders"];
		},
		countPending: function () {
			return this.statusOnlineCount(ORDER_STATUS.PENDING);
		},
		countAllPending: function () {
			return this.statusOnlineCount(ORDER_STATUS.PENDING) + this.statusOnlineCount(ORDER_STATUS.OPEN);
		},
		isLoad: function () {
			return this.$store.getters.isLoad;
		},
		orderHasOnlyGiftCardProduct(){
			const void_status = this.currentOrder.items.map(item => item.void_status).every(item => item === null)
			if (!void_status) return this.currentOrder.items.filter(item => item.void_status === 0).every(item=>item.product === -2);
			else return false;
		},
		isMobile: function () {
			return window.innerWidth <= 992;
		},
    hasUnreadFutureOrders() {
      if (!this.orders) return false;
      // Any Pending online order scheduled for a day after today
      return this.orders.some(o =>
          ONLINE_SOURCE_ORDER_TYPES.includes(o.type)
          && o.serve_at
          && this.isFutureDay(o.serve_at)
          && o.status === ORDER_STATUS.PENDING
      );
    },
    // Today/Future split only applies to actionable statuses; completed,
    // cancelled and refunded orders are shown regardless of date.
    showDateTabs() {
      return this.statusFilter === ORDER_STATUS.PENDING
        || this.statusFilter === ORDER_STATUS.OPEN
        || this.statusFilter === this.ALL_PENDING;
    },
		// Class-name overrides for the shared OrderListCard so this view keeps its
		// existing `oti-*` / `oo-*` markup (and therefore its existing CSS) intact.
		cardClasses() {
			return {
				card: "oo-card",
				header: "oti-header",
				headerLeft: "oti-header-left",
				id: "oti-id",
				customer: "oti-customer",
				amount: "oti-amount",
				amountLabel: "oti-amount-label",
				amountValue: "oti-amount-value",
				bottom: "oti-bottom",
				info: "oti-info",
				infoCell: "oti-info-cell",
				infoLabel: "oti-info-label",
				infoValue: "oti-info-value",
				actions: "oo-actions"
			};
		}
	},
	watch: {
		async statusFilter(status, prevStatus) {
			if (status === this.ALL_PENDING) {
				// "All Pending" combines Pending (online) and Payment Pending (OPEN) orders
				await this.fetchOnlineOrders(ORDER_STATUS.PENDING)
				await this.fetchOrderListByStatus({
					orderStatus: ORDER_STATUS.OPEN,
					firstPage: 1,
					initial: true,
					onlineOrders: true
				})
				this.$store.dispatch("getList", { status: ORDER_STATUS.PENDING, firstPage: true });
				this.$store.dispatch("resetPageCounterForStatus", { prevStatus });
				this.countItems = this.perPage;
				return;
			}
			if (status !== 0) {
				const payload = {
					orderStatus: status,
					firstPage: 1,
					initial: true,
					onlineOrders: true
				}
				await this.fetchOrderListByStatus(payload)
			}
			else {
				await this.fetchOnlineOrders(status)
			}
			this.$store.dispatch("getList", { status, firstPage: true });
			this.$store.dispatch("resetPageCounterForStatus", { prevStatus });
			this.countItems = this.perPage;
		},
		searchValue (value, prevValue) {
			const idRegex = "^[0-9_]*$";
			if (value.search(idRegex) === -1) this.searchValue = prevValue;
		},
		// Phase 5: side effects that used to live inside the filteredOrders
		// computed are reproduced here so the computed stays pure while preserving
		// the original cadence (they ran on every completed-list re-evaluation):
		//   1. resetting the `deductRefundQuantity` flag, and
		//   2. the partial/full-refund reconciliation that stamps display flags on
		//      the store order objects (so they carry over when an order is
		//      previewed as currentOrder). See helpers/order/partialRefund.js.
		// The reconciliation runs over the pre-pagination filtered set (matching
		// the original, which ran before the sort/splice).
		filteredOrders () {
			if (this.statusFilter === ORDER_STATUS.COMPLETED) {
				this.setDeductRefundQuantity(false);
				reconcilePartialRefunds(this.buildFilteredOrders(), this.refundedOrdersList);
			}
		}
	},
	async created () {
		if (this.products.length === 0){
			await this.fetchProductsFromDb()
		}
	},
	async mounted () {
		this.startTimer();
		if (this.statusFilter === this.ALL_PENDING) {
			// "All Pending" combines Pending (online) and Payment Pending (OPEN) orders
			await this.fetchOnlineOrders(ORDER_STATUS.PENDING)
			await this.fetchOrderListByStatus({
				orderStatus: ORDER_STATUS.OPEN,
				firstPage: 1,
				initial: true,
				onlineOrders: true
			})
			await this.$store.dispatch("getList", { status: ORDER_STATUS.PENDING, firstPage: true });
		} else {
			await this.fetchOnlineOrders(this.statusFilter)
			await this.$store.dispatch("getList", { status: this.statusFilter, firstPage: true });
		}
		// Periodically refresh (every 3 minutes) so newly arrived online orders
		// appear without the user having to toggle tabs. Cleared in beforeDestroy.
		this.syncing = setInterval(this.refreshOrders, 180000);
		// Re-bucket Today/Future when the calendar day rolls over (independent of
		// the polling above). The visibility check covers the machine sleeping
		// through midnight and waking on a later day.
		this.scheduleMidnightRollover();
		document.addEventListener("visibilitychange", this.handleVisibilityChange);
		// Refresh immediately whenever a pusher orders event arrives so new
		// online orders show up without waiting for the polling interval.
		this.$root.$on("orders-event", this.refreshOrders);
		const container = document.querySelector("#infinite-list");
		const self = this;
		if (container) {
			// Store element + handler so the listener can be detached in
			// beforeDestroy (previously it was never removed -> leak).
			this.scrollContainer = container;
			this.onScrollHandler = async function () {
				if (self.isLoad) return false;
				const userScrollHeight = container.scrollTop + container.clientHeight;
				const threshold = userScrollHeight + container.scrollHeight * 0.2;
				if (threshold - container.scrollHeight > -2) {
					await self.$store.dispatch("getList", { status: self.statusFilter });
					self.countItems = self.countItems + self.perPage;
				}
			};
			container.addEventListener("scroll", this.onScrollHandler);
		}
	},
	beforeDestroy () {
		clearInterval(this.syncing);
		clearTimeout(this.midnightTimer);
		document.removeEventListener("visibilitychange", this.handleVisibilityChange);
		this.$root.$off("orders-event", this.refreshOrders);
		// Detach the infinite-scroll listener (Phase 5 leak fix).
		if (this.scrollContainer && this.onScrollHandler) {
			this.scrollContainer.removeEventListener("scroll", this.onScrollHandler);
			this.scrollContainer = null;
			this.onScrollHandler = null;
		}
		// Clear the startup interval if still running (Phase 5 leak fix).
		if (this.startupTimer) {
			clearInterval(this.startupTimer);
			this.startupTimer = null;
		}
		this.$store.dispatch("resetPageCounterForAll");
	},
	methods: {
		...mapActions('orders', ['setRefundItems', 'setRefundOrderId', 'setDeductRefundQuantity', 'setRefundOrderTableId', 'setRefundedOrderTotalDiscount','fetchOnlineOrders','fetchOrderListByStatus']),
		...mapActions('config',['fetchProductsFromDb']),
		toFixedNoRounding,
		// Shared pure list helpers (see helpers/order/listFormatting.js)
		getTimerValue,
		formatServeAt,
		// Single pricing engine: grand_total + surcharge_value + tips_total
		orderFinalTotal(order) {
			return getOrderFinalTotal(order);
		},
		// A KitchenHub order paid on the delivery platform has no local Payment
		// record until the cashier actually clicks "Complete" (completeKitchenHubOrder
		// builds it at that point) — order.payments.length alone would show "Unpaid"
		// the whole time even though the customer already paid, so fall back to
		// is_kitchenhub_paid for those.
		isOrderPaid (order) {
			return !!((order.payments && order.payments.length) || order.is_kitchenhub_paid);
		},
		async pay (order) {
			await this.$router.push({
				name: "OrderCheckout",
				params: { id: (order.id || order.local_id) }
			});
		},
		// KitchenHub orders paid on the delivery platform (order.is_kitchenhub_paid)
		// have nothing left to collect on the POS, so this replaces the Pay screen:
		// build the Payment record from the order totals KitchenHub already gave us
		// (subtotal/tips/grand_total, taken from `charges` at build time) and
		// complete directly. Syncing to CLOSED pushes "complete" to KitchenHub via
		// the existing sync_kitchenhub_order_status_if_required hook.
		completeKitchenHubOrder (order) {
			const currentShift = this.$store.getters.shift;
			const paymentObject = {
				order: order.id || order.local_id,
				employee: this.employee ? this.employee.id : null,
				amount: order.grand_total,
				amount_paid: order.grand_total,
				tips: order.tips_total || 0,
				change_amount: 0,
				// Explicitly mapped to the seeded UberEats/DoorDash PaymentType by
				// KitchenHub's stable provider id, not just the free-text provider name.
				payment_type: resolveKitchenHubPaymentTypeName(order),
				payment_date: new Date().toISOString(),
				transaction_type: TRANSACTION_TYPE.SALE,
				// Payment.payment_type has no FK back to PaymentType, so this is the
				// only place on the Payment row that ties back to the exact seeded
				// PaymentType by id (-1 UberEats, -2 DoorDash) rather than just its name.
				processor_response: String(resolveKitchenHubPaymentTypeId(order) ?? ""),
				card_lastfour: null,
				// Same terminal_id + index + timestamp convention StandardOrderCheckout.vue uses for every other payment.
				local_id: `${this.terminal.id}_${(order.payments ? order.payments.length : 0) + 1}_${Date.now()}`,
				shift: currentShift ? currentShift.id : null
			};
			const completedPayload = {
				...order,
				status: ORDER_STATUS.COMPLETED,
				employee: this.employee.id,
				related_terminal: this.terminal.id,
				close_date: new Date().toISOString(),
				receipt_kitchen_printed: true,
				payments: [paymentObject]
			};
			this.$store.dispatch("orders/saveOrder", completedPayload);
			this.currentOrder = order;
			this.$store.dispatch("orders/enqueueOrderSync", { order: completedPayload, trigger: "kitchenhub_order_complete" })
				.then(() => this.$store.dispatch("orders/drainOrderSyncOutbox"))
				.catch(() => {});
			this.$store.dispatch("sync");
		},
		approve (order) {
			order.items.map(item => {
				const productConfig = this.products.find(product => product.id === item.product)
				if (productConfig && productConfig.printers.length > 0) {
					item.printers = productConfig.printers
					item.has_kitchen_updates = !item.sent_to_kitchen && !!(productConfig.printers.length)
				}
			})
			if (order.payments.length) {
				// Read live state rather than the passed object, which may be a stale
				// snapshot from before auto-send marked receipt_kitchen_printed = true.
				const liveOrder = this.$store.state.orders.orders.find(o =>
					(order.id != null && o.id === order.id) ||
					(order.local_id != null && o.local_id === order.local_id)
				);
				const alreadyPrinted = liveOrder ? liveOrder.receipt_kitchen_printed : order.receipt_kitchen_printed;
				const completedPayload = {
					...order,
					status: ORDER_STATUS.COMPLETED,
					employee: this.employee.id,
					related_terminal: this.terminal.id,
					close_date: new Date().toISOString(),
					receipt_kitchen_printed: true
				};
				this.$store.dispatch("orders/saveOrder", completedPayload);
				this.currentOrder = order;
				if (!alreadyPrinted) {
					this.printKitchenCheck('online-order-paid');
				}
				this.$store.dispatch("orders/enqueueOrderSync", { order: completedPayload, trigger: "online_order_approve" })
					.then(() => this.$store.dispatch("orders/drainOrderSyncOutbox"))
					.catch(() => {});
			} else {
				const openPayload = {
					...order,
					status: ORDER_STATUS.OPEN,
					employee: this.employee.id,
					related_terminal: this.terminal.id
				};
				this.$store.dispatch("orders/saveOrder", openPayload);
				this.currentOrder = order;
				this.$store.dispatch("orders/enqueueOrderSync", { order: openPayload, trigger: "online_order_approve" })
					.then(() => this.$store.dispatch("orders/drainOrderSyncOutbox"))
					.catch(() => {});
			}
			this.$store.dispatch('sync');
		},
		reject (order) {
			this.confirmActionModal = {
				title: "Reject this order?",
				text: "Are you sure you want to reject this order?",
				confirmActionText: "yes",
				cancelActionText: "no"
			};
			this.actionToConfirm = "rejectConfirm";
			this.$bvModal.show("confirm-modal");
			this.rejectingOrder = order;
		},
		async rejectConfirm () {
			if (this.rejectingOrder.payments.length > 0) {
				const refundedPayload = {
					...this.rejectingOrder,
					status: ORDER_STATUS.CANCELLED,
					employee: this.employee.id
				};
				this.$store.dispatch("orders/saveOrder", refundedPayload);
				this.$store.dispatch("orders/enqueueOrderSync", { order: refundedPayload, trigger: "online_order_reject_cancel" })
					.then(() => this.$store.dispatch("orders/drainOrderSyncOutbox"))
					.catch(() => {});
				this.$bvModal.hide("confirm-modal");
				try {
					await ApiService.post('orderlive/refundorder', { id: this.rejectingOrder.id });
				} catch (error) {
					console.error('Error refunding order:', error);
				}
			} else {
				const cancelledPayload = {
					...this.rejectingOrder,
					status: ORDER_STATUS.CANCELLED,
					employee: this.employee.id
				};
				this.$store.dispatch("orders/saveOrder", cancelledPayload);
				this.$store.dispatch("orders/enqueueOrderSync", { order: cancelledPayload, trigger: "online_order_reject_cancel" })
					.then(() => this.$store.dispatch("orders/drainOrderSyncOutbox"))
					.catch(() => {});
				this.$bvModal.hide("confirm-modal");
			}
		},
		confirmAction (value) {
			if (!value) return false;
			this[this.actionToConfirm]();
			this.$bvModal.hide("confirm-action--modal");
		},
		// Mirrors the /orders (OrdersList) cancel flow so an approved (OPEN/HOLD)
		// online order can be cancelled directly from this view. Relies on the
		// shared orderMixin (cancelOrderModalPromise / cancelOrderProcessing).
		async cancelCurrentOrder () {
			const identifier = this.currentOrder?.id || this.currentOrder?.local_id;
			if (!identifier) return;

			let fullOrder = this.$store.getters["orders/getOrderByIdOrLocalId"](identifier);
			if (!fullOrder) fullOrder = { ...this.currentOrder };

			fullOrder = JSON.parse(JSON.stringify(fullOrder));
			fullOrder.local_id = fullOrder.local_id || fullOrder.id;
			fullOrder.items = Array.isArray(fullOrder.items) ? fullOrder.items : [];
			fullOrder.payments = Array.isArray(fullOrder.payments) ? fullOrder.payments : [];
			if (fullOrder.id) {
				fullOrder.number = fullOrder.id.toString();
			}

			const previousOrder = this.currentOrder;
			let cancelled = false;
			this.currentOrder = fullOrder;
			try {
				// Void-reason modal doubles as the confirmation when reasons are
				// configured (see confirmOrderCancel in orderMixin).
				const { confirmed, voidReason } = await this.confirmOrderCancel();
				if (!confirmed) return;
				this.setSelectedOrder(identifier);
				// Stay on the Online Orders view after cancelling.
				await this.cancelOrderProcessing({ skipNavigation: true, voidReason });
				cancelled = true;
			} finally {
				if (!cancelled) {
					this.currentOrder = previousOrder;
				}
				this.setSelectedOrder(null);
			}
		},
		showRefundModal() {
			const self = this
			const orderToBeRefunded = { ...this.currentOrder }
			orderToBeRefunded.items.forEach(item=>item.duplicateQuantity = item.quantity)
			let refundedOrders = [...self.refundedOrdersList]
			refundedOrders = refundedOrders.filter(refundedOrders => refundedOrders.reference_order === orderToBeRefunded.id)
			if (refundedOrders.length > 0) {
				refundedOrders.forEach(refundedOrder => {
					orderToBeRefunded.items.forEach((orderToBeRefundedItems, itemIndex) => {
						const item = refundedOrder.items.filter(refundedOrderItem => refundedOrderItem.order_refund_item === orderToBeRefundedItems.id)

						if (item.length > 0 && item[0].void_item)
							orderToBeRefunded.items[itemIndex].quantity += item[0].quantity
						if (orderToBeRefunded.items[itemIndex].quantity <= 0) {
							orderToBeRefunded.items.splice(itemIndex, 1)
						}
					})
				})
			}
			// saving the attributes which calculates discount total for the refunded order of the reference order
			const discountTotalDetails = {
				value: this.currentOrder.discount_orders_raw_value,
				discountType: this.currentOrder.discount_orders_raw_type

			}
			this.setRefundItems(orderToBeRefunded.items || [])
			// stroing in store
			this.setRefundedOrderTotalDiscount(discountTotalDetails)
			this.setRefundOrderId(this.currentOrder.id)
			this.setRefundOrderTableId(this.currentOrder.table)
			this.$router.push({ name: 'Order', params: { id: 'new', tableId: null, referenceId: this.currentOrder.id, refund: true } })

			// this.confirmActionModal = {
			// 	title: "Refund",
			// 	text: "Are you sure you want to refund this order",
			// 	confirmActionText: "refund"
			// };
			// this.actionToConfirm = "refund";
			// this.$bvModal.show("confirm-action--modal");
		},
		async refund () {
			this.$bvModal.show("processing-modal");
			let isError = false;
			const paymentObjects = [];
			try {
				for (const payment of this.currentOrder.payments) {
					const amount = 0 - (payment.amount - payment.change_amount);
					const current_payment_type = this.terminal.payment_types.find(py => py.name === payment.payment_type);
					let result;
					if (!current_payment_type) {
						this.error.title = "Payment type is not found";
						this.error.info = "Payment type is not available at current terminal";
						isError = true;
						break;
					}
					const behavior = current_payment_type.behavior;
					if (behavior === PAYMENT_BEHAVIOR.NABANCARD_INTEGRATION) {
						// NA payment
						// try void
						try {
							result = await makeNAVoid(current_payment_type.url, payment.processor_response);
							if (result && result.status === "error") {
								throw Error("Could not refund");
							}
						} catch (e) {
							console.error("Could not void");
							console.error(e);
							isError = true;
						}
						// try refund
						if (!result || (result && result["status"] === "error")) {
							try {
								result = await makeNARefund(current_payment_type.url, payment.processor_response);
								if (result && result.status === "error") {
									throw Error("Could not refund");
								}
							} catch (e) {
								console.error("Could not refund");
								console.error(e);
								isError = true;
							}
						}
						if (!result || (result && result["status"] === "error")) {
							console.error("Can not refund");
							this.error.title("Can not refund");
							this.error.info("Can not refund, contact administrator");
							isError = true;
						}
						// NA payment end
					}
					if (!isError) {
						paymentObjects.push({
							order: payment.order,
							employee: payment.employee,
							amount: amount,
							amount_paid: amount,
							tips: payment.tips,
							change_amount: 0,
							payment_type: payment.payment_type,
							payment_date: new Date().toISOString(),
							related_terminal: this.terminal.id,
							transaction_type: TRANSACTION_TYPE.RETURN,
							processor_refund_response: result && result.response ? result.response : "",
							card_lastfour: null,
							local_id: `${this.terminal.id}_${this.currentOrder.payments.length + 1 + paymentObjects.length}`,
							shift: this.shift.id
						});
					}

				}
			} catch (e) {
				console.error(e);
				isError = true;
			}
			this.$bvModal.hide("processing-modal");
			if (isError) {
				this.$bvModal.show("modal-error-checkout");
			} else {
				this.currentOrder.status = 5;
				this.currentOrder.payments = [...this.currentOrder.payments, ...paymentObjects];
				paymentObjects.map(async (paymentObject) => {
					await this.savePaymentToShift({ name: paymentObject.payment_type, amount: paymentObject.amount }, "refund");
				});
				await this.saveAndStartSyncing();
				this.$bvModal.show("success-action--modal");
			}

		},
		async searchByNumber () {
			const id = this.searchValue;
			if (isNaN(id)) {
				if (!id.includes("_")) return this.searchError();
				return this.localSearch(id);
			}
			if (!navigator.onLine) return this.localSearch(id);

			const orders = await this.$store.dispatch("getOrder", { id });
			if (!orders.length) return this.searchError();
			this.currentOrder = orders[0];

			return this.searchValue = "";

		},
		localSearch (id) {
			const orders = this.orders.filter(order => order.id == id);
			if (!orders.length) return this.searchError();

			this.currentOrder = orders[0];

			return this.searchValue = "";
		},
		searchError () {
			sound.play();
			this.infoModal.text = "Order not found";
			this.$bvModal.show("info-modal");

			return this.searchValue = "";
		},
		receiveMessage (event) {
			const self = this;
			self.updateLocalId(event.data);
		},
		startTimer () {
			const vm = this;
			// Track the interval id so beforeDestroy can clear it. (Previously this
			// interval referenced an undefined `value`, so it never self-cleared and
			// leaked a 50ms timer for the lifetime of the page.)
			this.startupTimer = setInterval(function () {
				vm.timer += 2;
				if (vm.timer >= 180) {
					clearInterval(vm.startupTimer);
					vm.startupTimer = null;
				}
			}, 50);
		},
		updateLocalId: function (msgObj) {
			if (this.currentOrder.id === msgObj.localID) {
				this.currentOrder.id = msgObj.remoteID;
			}
		},
		calculateDiscount: function (itemPrice, discount) {
			if (!discount) return 0;
			if (itemPrice === 0) return 100;
			return parseFloat((discount / itemPrice * 100).toFixed(2));
		},
		computedBalanceRemaining: function (order) {
			if (!order) return 0;
			// Total is grand_total + surcharge_value + tips_total (single pricing
			// engine, see pricing/engine.js). Payment.amount/amount_paid mirror
			// grand_total only (tip/surcharge are tracked on their own Payment
			// fields), so they must be added back in here or a fully-paid
			// surcharge/tip order would never reconcile to a zero balance.
			const total = getOrderFinalTotal(order);
			const paid = (order.payments || []).reduce(
				(sum, p) => sum + (p.amount_paid || p.amount || 0) + (p.tips || 0) + (p.surcharge || 0),
				0
			);
			return parseFloat((total - paid).toFixed(2));
		},
		isDisplayPaymentDetailsList: function () {
			return this.displayPaymentDetailsList = !this.displayPaymentDetailsList;
		},
		isSelected (order) {
			if (!this.currentOrder || !order) return false;
			if (order.id != null && this.currentOrder.id != null) return order.id === this.currentOrder.id;
			if (order.local_id != null && this.currentOrder.local_id != null) return order.local_id === this.currentOrder.local_id;
			return false;
		},
		accentClass (order) {
			switch (order.status) {
				case ORDER_STATUS.PENDING: return "accent-pending";
				case ORDER_STATUS.OPEN: return "accent-open";
				case ORDER_STATUS.COMPLETED: return "accent-completed";
				case ORDER_STATUS.CANCELLED: return "accent-cancelled";
				case ORDER_STATUS.REFUNDED: return "accent-refunded";
				default: return "";
			}
		},
		// Pure helper shared by the filteredOrders computed and the filteredOrders
		// watcher: returns the type/status/dining/date-filtered, sorted online
		// orders WITHOUT pagination (splice) and WITHOUT the refund reconciliation.
		buildFilteredOrders () {
			const self = this;
			let orders = this.orders.filter(function (order) {
				if (!ONLINE_SOURCE_ORDER_TYPES.includes(order.type)) return false;
				return self.matchesStatusFilter(order.status);
			});
			// filter by dining
			orders = this.diningFilter ? orders.filter(o => o.dining_option === this.diningFilter) : orders;

			// filter by date period (only for statuses that show the date tabs)
			if (this.showDateTabs && this.dateFilter !== "all") {
				orders = orders.filter(o => this.orderInDateBucket(o, this.dateFilter));
			}

			return orders.sort((a, b) => {
				// Completed / Cancelled / Refunded (no date tabs): most recent first.
				if (!this.showDateTabs) {
					const aRecent = new Date(a.close_date || a.open_date).getTime();
					const bRecent = new Date(b.close_date || b.open_date).getTime();
					return bRecent - aRecent;
				}
				// Pending / Payment Pending: earliest serve time first.
				const aTime = new Date(a.serve_at || a.open_date).getTime();
				const bTime = new Date(b.serve_at || b.open_date).getTime();
				return aTime - bTime;
			});
		},
		// Builds the props-down meta-line array for the shared OrderListCard.
		// Mirrors the original inline `oti-address` markup exactly.
		buildMetaLines (order) {
			const lines = [];
			if (order.delivery_address) lines.push({ text: order.delivery_address, className: "oti-address" });
			return lines;
		},
		// Builds the props-down info-cell array for the shared OrderListCard.
		// Mirrors the original inline `oti-info-cell` markup exactly (order matters).
		buildInfoCells (order) {
			return [
				{ label: "Serve at", value: this.formatServeAt(order.serve_at), valueClass: "oti-serve" },
				{ label: "Time Elapsed", value: this.getTimerValue(order.open_date, order.close_date) },
				{ label: "Ordered", value: new Date(order.open_date).toLocaleTimeString("en-US", { timeStyle: "short" }) },
				{ label: "Items", value: order.items.length }
			];
		},
		// An order belongs in "future" when it is scheduled (serve_at) for a
		// calendar day after today. Everything else — ASAP orders and orders due
		// today or overdue — belongs in "today" so nothing silently disappears
		// once its scheduled day arrives.
		orderInDateBucket (order, bucket) {
			const isFuture = !!(order.serve_at && this.isFutureDay(order.serve_at));
			return bucket === "future" ? isFuture : !isFuture;
		},
		// Day-granularity comparison against the reactive currentDay so the
		// computed lists/counts re-evaluate when the day rolls over.
		isFutureDay (dateValue) {
			if (!dateValue) return false;
			const todayStart = new Date(this.currentDay);
			todayStart.setHours(0, 0, 0, 0);
			const d = new Date(dateValue);
			if (isNaN(d.getTime())) return false;
			const dStart = new Date(d.getFullYear(), d.getMonth(), d.getDate());
			return dStart.getTime() > todayStart.getTime();
		},
		// Schedule a single timeout that fires just after the next local midnight,
		// flips currentDay (triggering a re-bucket), then reschedules itself.
		scheduleMidnightRollover () {
			clearTimeout(this.midnightTimer);
			const now = new Date();
			const nextMidnight = new Date(now);
			nextMidnight.setHours(24, 0, 0, 0);
			this.midnightTimer = setTimeout(() => {
				this.currentDay = new Date().toDateString();
				this.scheduleMidnightRollover();
			}, (nextMidnight.getTime() - now.getTime()) + 1000);
		},
		// Covers the machine sleeping through midnight: when the tab becomes
		// visible again on a later day, refresh currentDay and realign the timer.
		handleVisibilityChange () {
			if (document.hidden) return;
			const day = new Date().toDateString();
			if (day !== this.currentDay) {
				this.currentDay = day;
				this.scheduleMidnightRollover();
			}
		},
		// Whether an order status belongs in the currently selected tab. The
		// "All Pending" tab combines Pending and Payment Pending (OPEN) orders.
		matchesStatusFilter (status) {
			if (this.statusFilter === this.ALL_PENDING) {
				return status === ORDER_STATUS.PENDING || status === ORDER_STATUS.OPEN;
			}
			return status === this.statusFilter;
		},
		// Total online orders in a given status (drives the status-tab count badges).
		statusOnlineCount (status) {
			if (!this.orders) return 0;
			return this.orders.filter(o => ONLINE_SOURCE_ORDER_TYPES.includes(o.type) && o.status === status).length;
		},
		// Online orders for the currently selected status that fall in a date
		// bucket (today/future), respecting the active dining filter. Drives the
		// Today/Future count badges so staff can see where orders are, and the
		// count drops as orders are handled.
		dateBucketCount (bucket) {
			if (!this.orders) return 0;
			return this.orders.filter(o =>
				ONLINE_SOURCE_ORDER_TYPES.includes(o.type)
				&& this.matchesStatusFilter(o.status)
				&& (!this.diningFilter || o.dining_option === this.diningFilter)
				&& (bucket === "all" || this.orderInDateBucket(o, bucket))
			).length;
		},
		refreshOrders () {
			if (!window.navigator.onLine) return;
			// Unified refresh policy (Phase 5): when the Ably realtime channel is
			// connected, order updates stream in over realtime, so the 3-minute
			// poll and the Pusher "orders-event" refetch are redundant and would
			// double-fetch. Skip them here and rely on realtime. When Ably is
			// disconnected this guard is false, so polling/Pusher resume as the
			// fallback (this also handles Ably dropping mid-session).
			if (this.isRealtimeActive) return;
			// Always refresh pending online orders so new arrivals are detected on
			// any tab (this also drives the auto-send-to-kitchen flow). When the
			// active tab is not Pending, refresh that status too.
			this.fetchOnlineOrders(ORDER_STATUS.PENDING);
			// The "All Pending" tab also needs Payment Pending (OPEN) orders.
			if (this.statusFilter === this.ALL_PENDING) {
				this.fetchOrderListByStatus({
					orderStatus: ORDER_STATUS.OPEN,
					firstPage: 1,
					initial: true,
					onlineOrders: true
				});
			} else if (this.statusFilter !== ORDER_STATUS.PENDING) {
				this.fetchOrderListByStatus({
					orderStatus: this.statusFilter,
					firstPage: 1,
					initial: true,
					onlineOrders: true
				});
			}
		}
	}
};
</script>

<style lang="scss">
.first {
	border-radius: 5px 0px 0px 5px !important;
}
.last {
	border-radius: 0px 5px 5px 0px !important;
}
.middle {
	border-radius: 0 !important;
}
.first-col {
	margin: 1px 0 !important;
}
.middle-col {
	margin: 1px 0 4px 1px !important;
}
.badge-p {
	position: absolute;
    left: -3px;
    top: -4px;
    z-index: 1;
    border-radius: 50%;
    background-color: #F44336;
    width: 22px;
    height: 22px;
    padding: 5px;
    font-size: 13px;
}
// Date filter tabs (Today/Future) mirror the top status tabs: the badge is a
// sibling of the button inside a position-relative wrapper so it renders on
// top of the button instead of being clipped inside the btn-group.
.date-tab-group {
	display: inline-flex;

	.date-tab {
		position: relative;
	}

	.date-tab + .date-tab .btn {
		margin-left: -1px;
	}
}
// Let the tab buttons size to their label (the global .filter-panel .btn rule
// forces fixed widths at breakpoints, which makes "Payment Pending" wrap).
.orders .filter .filter-panel .date-tab-group .btn {
	width: auto;
	min-width: 90px;
	padding-left: 16px;
	padding-right: 16px;
	white-space: nowrap;
}
.tab-count {
	position: absolute;
	top: -6px;
	right: -6px;
	z-index: 900;
	min-width: 20px;
	height: 20px;
	padding: 0 6px;
	line-height: 20px;
	text-align: center;
	border-radius: 10px;
	background-color: #F44336;
	color: #fff;
	font-size: 11px;
	font-weight: 600;
}
.filter-p {
	padding: 10px 24px !important;
}
.orders .filter .filter-panel.filter-panel-sub {
	padding-top: 10px;
	overflow: visible;
	background: #FAFAFA;
}
.oo-list {
	display: flex;
	flex-direction: column;
	gap: 12px;
	padding: 6px 4px 16px;

	.oo-empty {
		padding: 32px 16px;
		font-size: 15px;
	}

	.oo-card {
		background: #fff;
		color: #1E1E1E;
		border: 1px solid #e6e6e6;
		border-left: 4px solid #d0d0d0;
		border-radius: 8px;
		padding: 14px 16px;
		cursor: pointer;
		transition: box-shadow .15s ease, border-color .15s ease, background-color .15s ease;

		// Keep text readable on hover (override any global row hover that turns text white)
		&:hover {
			border-color: #c4c4c4;
			background: #fff;
			color: #1E1E1E;
			box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
		}

		&.active {
			border-color: #2A7BFF;
			background: #f0f6ff;
			box-shadow: 0 2px 10px rgba(42, 123, 255, 0.12);
		}

		// Status accent on the left edge
		&.accent-pending { border-left-color: #F5A623; }
		&.accent-open { border-left-color: #2A7BFF; }
		&.accent-completed { border-left-color: #2E9E5B; }
		&.accent-cancelled { border-left-color: #9e9e9e; }
		&.accent-refunded { border-left-color: #E2574C; }
	}

	.oti-header {
		display: grid;
		grid-template-columns: minmax(155px, auto) minmax(0, 1fr) auto;
		align-items: flex-start;
		gap: 12px;
	}

	.oti-header-left {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 8px;
		grid-column: 1;
	}

	.oti-id {
		font-size: 16px;
		font-weight: 700;
		color: #1E1E1E;
	}

	.oti-dining {
		font-size: 14px;
		color: #6b6b6b;
	}

	.oti-source-pill {
		display: inline-block;
		padding: 2px 10px;
		border-radius: 12px;
		font-size: 11px;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: .3px;
	}

	.oti-source-kitchenhub {
		background: #eef2ff;
		color: #4338ca;
	}

	.oti-amount {
		display: flex;
		flex-direction: column;
		text-align: right;
		line-height: 1.2;
		white-space: nowrap;
		grid-column: 3;
	}

	.oti-amount-label {
		font-size: 12px;
		color: #8a8a8a;
	}

	.oti-amount-value {
		font-size: 18px;
		font-weight: 700;
		color: #1E1E1E;
	}

	.oti-customer {
		font-size: 14px;
		color: #4a4a4a;
		min-width: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		grid-column: 2;
	}

	.oti-address {
		margin-top: 6px;
		font-size: 13px;
		color: #6b6b6b;
	}

	.oti-bottom {
		display: flex;
		flex-wrap: wrap;
		justify-content: space-between;
		align-items: flex-end;
		gap: 12px 16px;
		margin-top: 14px;
	}

	.oti-info {
		display: flex;
		flex-wrap: wrap;
		gap: 14px 28px;
		flex: 1 1 260px;
		min-width: 0;
	}

	.oti-info-cell {
		display: flex;
		flex-direction: column;
		line-height: 1.3;
	}

	.oti-info-label {
		font-size: 12px;
		color: #8a8a8a;
	}

	.oti-info-value {
		font-size: 15px;
		font-weight: 600;
		color: #1E1E1E;

		&.oti-serve {
			color: #C0392B;
		}
	}

	.oti-status-pill {
		display: inline-block;
		padding: 2px 10px;
		border-radius: 12px;
		font-size: 11px;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: .3px;

		&.paid {
			background: #e6f4ea;
			color: #1e7e34;
		}

		&.unpaid {
			background: #fdecea;
			color: #c0392b;
		}
	}

	.oo-actions {
		display: flex;
		gap: 8px;
		flex: 0 0 auto;
		flex-wrap: nowrap;
		justify-content: flex-end;
		margin-left: auto;
		align-self: flex-end;

		.btn {
			flex: 0 0 120px;
			width: 120px;
			min-width: 0;
			margin: 0;
			white-space: nowrap;
		}
	}
}
@media (max-width: 767.98px) {
	.filter-p {
		padding: 2px 20px !important;
	}
}
@media (max-width: 992px) {
	.filter-p {
		padding: 2px 20px !important;
	}
}
.orders-bottom-options {
	border-bottom: 1px solid #e0e0e0;
}

.list-complete-item {
	transition: all .5s;
}

.list-complete-enter, .list-complete-leave-to {
	opacity: 0;
	transform: translateY(30px);
}

.list-complete-leave-active {
	position: absolute;
}

.popup-price {

	.modal-dialog {
		max-width: 440px;
	}

	.modal-body {
		padding: 30px 44px;

		@media (max-width: 767.98px) {
			padding: 25px 12px;
		}
	}

	.modal-header {
		background: #FAFAFA;
		align-items: center;
		position: relative;
		justify-content: center;
		height: 72px;

		.btn {
			min-width: 84px;
			position: absolute;
			left: 16px;
			top: 50%;
			margin-top: -20px;

			@media (max-width: 767.98px) {
				left: 12px;
			}
		}

		h5 {
			margin: 0px;
			text-align: center;
		}
	}

	.modal-footer {

		border: none;
		position: absolute;
		width: 130px;
		text-align: center;
		top: 0;
		bottom: 0;
		background: #EBEBEB;
		border-radius: 0px 10px 10px 0px;
		right: -130px;
		border-left: 3px solid #2A7BFF;
		padding: 0px;
		align-items: flex-start;
		overflow: hidden;
		height: 100%;

		.w-100 {
			height: 100%;
		}

		h5 {
			font-size: 16px;
			line-height: 19px;
			color: rgba(74, 74, 74, 0.717108);
			background: #FAFAFA;
			height: 71px;
			line-height: 71px;
			margin: 0px;
			width: 100%;
		}

		ul {
			overflow-y: auto;
			height: calc(100% - 71px);

			li {
				padding: 1px 0;
			}

			a {
				display: block;
				height: 72px;
				width: 100%;
				font-size: 15px;
				line-height: 21px;
				color: #1E1E1E;
				text-decoration: none;
				background: #fff;
				transition: all .2s ease-in-out;
				padding: 10px;

				&.active,
				&:hover {
					background: #2889FA;
					color: #fff;
				}

				span {
					display: block;
					position: relative;
					top: 50%;
					transform: translateY(-50%);
				}
			}


		}
	}

	&-sidebar {
		.modal-content {
			border-radius: 10px 0px 0px 10px;
		}

		.modal-header {
			border-radius: 10px 0px 0px 0rem;
		}

		.modal-body {
			border-radius: 0rem 0px 0px 10px;
		}
	}
}


.modifiers-sidebar-backdrop {
	background: #212121;
	width: 100%;
	position: absolute;
	top: 0;
	left: 0;
	bottom: 0;
	cursor: pointer;
	opacity: .5;
	z-index: 10;

	transition: all .2s ease;
}

.modifiers-sidebar-backdrop[aria-pressed="false"] {
	opacity: 0;
	z-index: -1;
}


.slide-enter-active,
.slide-leave-active {
	left: 0;
	transition: transform 0.2s ease;
}

.slide-enter,
.slide-leave-to {
	left: 100%;
	transition: all .15s ease-in 0s;
}


@media (max-width: 767.98px) {
	.list-complete-item,
	.modifiers-sidebar-backdrop,
	.slide-enter-active,
	.slide-leave-active,
	.slide-enter,
	.slide-leave-to {
		transition: none !important;
	}
}


.modifiers-sidebar-panel {
	position: absolute;
	z-index: 100;
	top: 0;
	background: #eeeeee;
	left: 0;
	width: 100%;
	bottom: 0;
}

.order-sum {
	background-color: #fff;
	color: #2c3e50;
	height: 60px;
	font-size: 1.375rem;
	line-height: 1.875rem;
	padding: 15px 16px;
}
.icon-dot{
  width: 8px;
  height: 8px;
  background-color: red;
  border-radius: 50px;
}
</style>
